/**
 * The shared account-state derivation (DNKADM-0255 Users roster; DNKADM-0256
 * Confirmations queue) — the ONE place an account-state badge is computed from the
 * server-computed `claimed` signal + membership `status` + `membershipState`, so
 * the two surfaces cannot drift on what "Unclaimed"/"Login only" mean.
 *
 * Split into its own module (not `MembersList.tsx`) per the DNKADM-0249 precedent:
 * a non-component export beside a component breaks Fast Refresh
 * (`react-refresh/only-export-components`). `MembersList.tsx` imports
 * {@link accountStateBadge} and {@link BadgeStyle}, so the dependency runs
 * component → helper and never back.
 *
 * The unclaimed guest stub (`claimed === false` — a guest-booking login, never
 * claimed, no password) gets its own distinct label and must NEVER read "Login
 * only". `claimed: true` means only "not an unclaimed guest stub", not that the
 * person can sign in, so it renders nothing by itself and falls through to the
 * membership-derived labels.
 *
 * MODE-AWARE since DNKADM-0269: the ink/tint pairs live in
 * `src/theme/maps/accountBadges.ts` (this feature dir is literal-free under the
 * 0269 ESLint guard). Callers pass `theme.palette.mode`; the default keeps
 * light — and every pre-0269 call site/test — byte-identical.
 */
import type { UserRow } from '@/api/users';
import { buildAccountBadgePairs } from '@/theme/maps/accountBadges';

/**
 * The three inputs the precedence reads (structurally a subset of {@link UserRow},
 * so the Users roster passes its rows unchanged and other surfaces — the
 * Confirmations queue, DNKADM-0256 — can supply just the signal fields).
 */
export type AccountStateSignal = Pick<UserRow, 'claimed' | 'status' | 'membershipState'>;

/** A chip's label + WCAG-remediated ink/tint pair, with an optional tooltip hint. */
export type BadgeStyle = {
  label: string;
  fg: string;
  bg: string;
  hint?: string;
};

/**
 * Derive the "Account state" badge for a roster row (DNKADM-0255), in precedence
 * order: unclaimed guest stub (`claimed === false`; never "Login only") →
 * membership status DISABLED → INVITED → the membership-state labels
 * (Active/Inactive member, else "Login only"). The "Login only" label is derived
 * from `membershipState === 'USER'`, not from `claimed: true`. An unknown future
 * `membershipState` renders raw (pre-0255 fallback, unchanged).
 */
export function accountStateBadge(
  user: AccountStateSignal,
  mode: 'light' | 'dark' = 'light',
): BadgeStyle {
  const pairs = buildAccountBadgePairs(mode);
  if (user.claimed === false) {
    return {
      label: 'Unclaimed',
      ...pairs.unclaimed,
      hint: 'Booked as a guest — hasn’t set a password yet, can’t sign in',
    };
  }
  if (user.status === 'DISABLED') return { label: 'Disabled', ...pairs.disabled };
  if (user.status === 'INVITED') return { label: 'Invited (pending)', ...pairs.invited };
  switch (user.membershipState) {
    case 'USER':
      return { label: 'Login only', ...pairs.membership.USER };
    case 'ACTIVE_MEMBER':
      return { label: 'Active member', ...pairs.membership.ACTIVE_MEMBER };
    case 'INACTIVE_MEMBER':
      return { label: 'Inactive member', ...pairs.membership.INACTIVE_MEMBER };
    default:
      return { label: user.membershipState, ...pairs.unknown };
  }
}

/**
 * Whether a roster row shows the separate "Email unconfirmed" badge (DNKADM-0410). Only
 * an explicit `emailVerified === false` on a row with a login (`claimed === true`)
 * qualifies. An unclaimed guest stub has no login and nothing to confirm, and a
 * `null` on either signal is unknown, so neither ever shows it. This never changes
 * {@link accountStateBadge}: "Login only"/"Unclaimed" say whether a login exists, a
 * different fact.
 */
export function showsEmailUnconfirmed(user: Pick<UserRow, 'claimed' | 'emailVerified'>): boolean {
  return user.claimed === true && user.emailVerified === false;
}

/**
 * The Confirmations-queue variant (DNKADM-0256): derive a badge from a reservation's
 * contact account signal, or **null when there is no badge to show**. Unlike the
 * roster (which always had a membership label to fall back to), the queue renders no
 * chip rather than guessing:
 *
 * - no signal at all (backend not shipped) → `null`;
 * - `claimed === false` → Unclaimed;
 * - `membershipState === 'USER'` → `null` unless the explicit false branch already
 *   fired — "USER" alone cannot distinguish a login-only row from an unclaimed
 *   stub, and `claimed: true` is not a sign-in promise;
 * - otherwise membership states render through {@link accountStateBadge}, with no
 *   membership `status` on this wire.
 */
export function contactAccountStateBadge(
  signal: {
    claimed: boolean | null;
    membershipState: string | null;
  },
  mode: 'light' | 'dark' = 'light',
): BadgeStyle | null {
  const { claimed, membershipState } = signal;
  if (claimed === false) {
    return accountStateBadge(
      { claimed, status: 'UNKNOWN', membershipState: membershipState ?? 'USER' },
      mode,
    );
  }
  if (membershipState === null) return null;
  if (membershipState === 'USER') return null;
  return accountStateBadge(
    { claimed, status: 'UNKNOWN', membershipState: membershipState ?? 'USER' },
    mode,
  );
}
